import type { ApartmentSteelAverage } from '@obraplan/core/domain/takeoff/apartment-average';
import {
  ArrowDownToLine,
  Check,
  ChevronDown,
  ChevronRight,
  Lock,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Trash2,
} from 'lucide-react';
import { useCallback, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  consolidationKey,
  resolveItemQuantity,
  type QuantityOrigin,
  type ResolvedQuantity,
  type TakeoffQuantities,
} from '@obraplan/core/domain/takeoff/consolidation';
import { totalFor, type GeometryTotals } from '@obraplan/core/domain/takeoff/geometry';
import {
  APPLIES_TO_LABEL,
  isAreaMeasure,
  isPendingElement,
  measureUnit,
  measureShortLabel,
  serviceElement,
  type AppliesTo,
} from '@obraplan/core/domain/takeoff/applies-to';
import { normalizeName } from '@obraplan/core/domain/shared/naming';
import { STAGE_REGISTRY } from '@obraplan/core/domain/takeoff/stage-registry';
import { serviceVolume } from '@obraplan/core/domain/takeoff/service';
import {
  hasImportedSheets,
  type CatalogDiscipline,
  type CatalogService,
  type ServiceCatalog,
} from '@obraplan/core/domain/takeoff/service-catalog';
import { ITEM_KIND_LABEL, type ItemKind } from '@obraplan/core/domain/takeoff/takeoff-line';
import {
  LABOR_SOURCE_LABEL,
  LABOR_WARNING_LABEL,
  resolveLaborItemRate,
} from '@obraplan/core/domain/takeoff/labor-rate';
import {
  materialTotal,
  ORIGIN_LABEL,
  resolveMaterialPrice,
  WARNING_LABEL,
} from '@obraplan/core/domain/takeoff/material-price';
import { unitLabel } from '@obraplan/core/domain/takeoff/service-quantity';
import {
  isSteelPurchaseService,
  STEEL_GAUGES,
  steelGaugeLabel,
  steelRatesFor,
  totalSteelRate,
  withSteelRate,
  type SteelGauge,
  type SteelRate,
} from '@obraplan/core/domain/takeoff/steel';
import { asId, type JobRoleId, type ServiceId } from '@obraplan/core/domain/shared/ids';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/feedback/states';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';
import { BuscaNoCompras } from './BuscaNoCompras';
import { CodigoDoItem } from './CodigoDoItem';
import { ComoFunciona } from './ComoFunciona';
import { CorrecaoComMotivo } from './CorrecaoComMotivo';
import { quantidadesDosServicos } from './medido-da-tipologia';
import { EXPLICACAO_DAS_PECAS, ROTULO_DAS_PECAS, type ServicoComPecas } from './pecas-importadas';
import { NumeroOpcionalInput } from './campos';
import { custoDasHoras, seloDaFonte } from './custo-da-mao-de-obra';
import { textoDoFator } from './fatores-proprios';
import { metros, numeroDaMedida, precoUnitario } from './formato';
import { algumSemFator, fatoresDoItemPorTipo, type FatorDoTipo } from './indice-por-tipo';
import type { ApartmentAverageForProject } from '@obraplan/core/application/use-cases/takeoff/rate-average-use-cases';
import type { IndexAverage } from '@obraplan/core/domain/takeoff/index-origin';
import { mesmaMedia } from './trazer-a-media';
import {
  atualizarComodosPelaMedia,
  atualizarServicoPelaMedia,
  fotoDaMedia,
  mediaDoItem,
  mudancasDoServico,
  mudancasDosComodos,
  atualizarTaxaPelaMedia,
  elementoDoAcoDaLista,
  elementosDaTaxa,
  fotoDaTaxa,
  mediaDaBitola,
  mudancasDaTaxa,
} from './trazer-a-media-do-apartamento';
import { ROOM_KIND_LABEL } from '@obraplan/core/domain/takeoff/room';
import { fatorDoProdutoDe, reais, ROTULO_CURTO_DA_ORIGEM } from './precos-da-obra';
import type { RoomFactors } from '@obraplan/core/domain/takeoff/room-quantity';
import {
  foundationSteelRates,
  type FoundationFactors,
} from '@obraplan/core/domain/takeoff/foundation-factors';
import { lancadosDoServico, quantitiesOf } from './resumo-dos-itens';
import { SugestaoDeProdutividade } from './SugestaoDeProdutividade';
import type { FuncaoDoServico, FuncoesDaObra } from './do-servidor';
import {
  acoAprovadoDa,
  aprovacaoDe,
  nomeDoAprovado,
  semOsAprovados,
  textoDaAprovacao,
  type AprovacaoDoServico,
  type ServicosAprovados,
} from './servicos-aprovados';
import { taxaDoServico, temListaPropria } from './taxa-por-elemento';
import type { TypologyDraft, WorkspaceAction } from './typology-draft';
import type { CustoDaMaoDeObraNaTela } from './use-custo-da-mao-de-obra';
import type { PrecosDaObraNaTela } from './use-precos-da-obra';
import { cn } from '@/lib/cn';

/**
 * O resumo dos itens, serviço por serviço — e onde a quantidade é decidida.
 *
 * É a tela em que o quantitativo vira o que esta obra vai consumir.
 *
 * ## A lista mostra o que tem material
 *
 * A obra chega com todos os serviços do cadastro — os dela e os globais (RN-007), que são
 * cento e cinquenta antes de qualquer planilha. Listá-los um a um, cada qual dizendo "0 de
 * 0 itens", produzia uma página de doze mil pixels em que os doze serviços com material
 * eram impossíveis de achar. Por isso a lista nasce filtrada no que **tem item**, e o
 * resto fica a um clique em "Todos" — que é onde se abre um serviço novo para dar material
 * a ele. A busca atravessa os dois, para quem sabe o nome do material e não sabe onde ele
 * mora.
 *
 * Os serviços saem **agrupados pela disciplina**, como a obra pensa: quem vai lançar
 * alvenaria lê a alvenaria inteira de uma vez, e não precisa de uma faixa de trinta e oito
 * chips para chegar lá. O cadastro de disciplina e serviço ficou na aba de montagem, que é
 * onde ele já morava: esta aba lança, não cadastra.
 *
 * ## Dois modos
 *
 * Sem planilha — a obra montada dentro do sistema — o número lançado é o único que existe,
 * e falar de "aba" ou de "o que a construtora orçou" seria a tela perguntando por algo que
 * não está lá. Com planilha, o assunto principal passa a ser a **comparação**: o campo
 * nasce com a quantidade da aba que orça esta tipologia; quem digita por cima está
 * discordando dela, e a tela diz isso. Campo vazio é "segue a planilha", e não zero: zerar
 * um item é uma decisão, apagar o campo é desfazer a sua.
 */

const ITEM_KINDS = Object.keys(ITEM_KIND_LABEL) as readonly ItemKind[];

/**
 * De onde veio a quantidade do item, como a linha diz. Um `Record` sobre a união: a origem nova
 * que o domínio acrescentar é erro de compilação aqui, e não uma linha muda na tela.
 */
const ROTULO_DA_ORIGEM: Readonly<Record<QuantityOrigin, string>> = {
  MANUAL: 'digitado',
  PECAS: ROTULO_DAS_PECAS,
  CALCULADO: 'calculado',
  PLANILHA: 'planilha',
};

const SELECT_CLASS =
  'h-9 rounded-md border border-(--color-border-subtle) bg-(--color-surface) px-1 text-xs';

/**
 * A medida do serviço com a peça, quando é ela que distingue: "mede por volume de pilar".
 *
 * Volume sem peça não diz de onde sai — e não sai de lugar nenhum até alguém escolher
 * (D17). A lista diz isso em vez de mostrar a grandeza como se medisse. O texto de cada par
 * mora em `measureShortLabel`: "faces de pilar" e "face" não são a mesma medida (fatia 10).
 */
function medidaCurta(servico: CatalogService): string {
  if (servico.measureBy === 'UNIT') return 'por unidade';
  if (servico.measureBy === 'MODEL') return 'mede pelo modelo';
  return isPendingElement(servico)
    ? 'escolha o elemento'
    : `mede por ${measureShortLabel(servico)}`;
}

const SEM_FUNCOES: FuncoesDaObra = {
  doServico: () => [],
  nome: () => undefined,
  idDoServico: () => undefined,
};

const SEM_TIPOS: ReadonlyMap<string, string> = new Map();

/** "26/10", de "2026-10-26". */
function diaEMes(dia: string): string {
  const [, mes, d] = dia.split('-');
  return `${d ?? ''}/${mes ?? ''}`;
}

/** Quantos itens do serviço ainda esperam número — o que "pendente" quer dizer aqui. */
function pendentesDoServico(servico: CatalogService, quantities: TakeoffQuantities): number {
  // Serviço sem item também espera: ele foi escolhido e ainda não diz o que consome.
  if (servico.items.length === 0) return 1;
  return servico.items.length - lancadosDoServico(servico, quantities);
}

const domId = (chave: string) => `servico-${chave.replace(/[^a-z0-9]+/gi, '-')}`;

export function ItensPorServico({
  catalog,
  catalogoDaObra = catalog,
  draft,
  tipologia,
  medidos,
  comodos,
  fundacao,
  dispatch,
  foraDoAndar = new Set<string>(),
  filtrado = false,
  onLimparFiltro,
  onAbrirEstrutura,
  funcoes = SEM_FUNCOES,
  precos,
  custo,
  aprovados,
  nomesDosTipos = SEM_TIPOS,
  tiposDeEstaca,
  mediaDoApartamento,
}: {
  /** O catálogo **da tipologia** — os escolhidos, das disciplinas do andar, já filtrados. */
  catalog: ServiceCatalog;
  /**
   * O catálogo **da obra** inteiro — onde o código informado procura os itens de mesma
   * descrição sem código (Fase 29, RN-396). Ausente, vale o da tipologia.
   */
  catalogoDaObra?: ServiceCatalog;
  /** As funções que a mão de obra pode apontar, por serviço (RN-252). */
  funcoes?: FuncoesDaObra;
  draft: TypologyDraft;
  /** O que o desenho determina para esta tipologia — a base de cada lançamento. */
  tipologia: GeometryTotals;
  /**
   * O medido de cada serviço nesta tipologia — o que o índice multiplica (Fase 24, fatia 6).
   * Ausente, nenhum item calcula, e a tela é a de antes do índice.
   */
  medidos?: Readonly<Record<string, number>>;
  /** Os cômodos com fator próprio e a medida de cada um (Fase 39, RN-512). Ausente: nenhum. */
  comodos?: RoomFactors;
  /** O bloco, o baldrame e o radier com fator, peça a peça (Fase 41, RN-539). Ausente: nenhum. */
  fundacao?: FoundationFactors;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** Escolhidos com número cuja disciplina saiu dos andares — ficam, com aviso. */
  foraDoAndar?: ReadonlySet<string>;
  /** O filtro do topo está ligado — o vazio diz isso, e não "nada escolhido". */
  filtrado?: boolean;
  onLimparFiltro?: () => void;
  onAbrirEstrutura?: () => void;
  /**
   * O preço de material da obra (Fase 29, etapa 2): cada linha mostra o preço do material e o
   * total dela, só para ler — quem decide o preço é a consolidação. Ausente, a tabela é a de
   * antes do orçamento.
   */
  precos?: PrecosDaObraNaTela;
  /**
   * O custo da mão de obra da obra (Fase 29, etapa 3): a linha de mão de obra mostra o R$/h da
   * função e o total, só para ler — o provisório se decide na consolidação. Só aparece com as
   * colunas do preço; ausente, a mão de obra mostra "—".
   */
  custo?: CustoDaMaoDeObraNaTela;
  /**
   * Os serviços aprovados no orçamento (Fase 33, RN-438): o serviço aprovado aparece com o
   * cadeado e só para ler — os itens, o índice, a perda, o código e a quantidade não mudam mais,
   * e o servidor recusaria. A taxa de aço da disciplina com serviço de aço aprovado também.
   */
  aprovados?: ServicosAprovados;
  /** O nome de cada tipo de estaca, pelo id — o índice da estaca é por tipo (Fase 39). */
  nomesDosTipos?: ReadonlyMap<string, string>;
  /** O botão que abre os Tipos de estaca, onde o fator de cada tipo muda (Fase 39). */
  tiposDeEstaca?: React.ReactNode;
  /**
   * A média do apartamento da obra (Fase 40, RN-520 a RN-522): a média ao lado do índice e a
   * faixa "a média mudou". Ausente, a tela é a de antes.
   */
  mediaDoApartamento?: ApartmentAverageForProject | undefined;
}) {
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  /**
   * As disciplinas que alguém abriu ou fechou à mão. As outras seguem a regra: abre a que
   * tem pendência, fecha a que está completa — a lista longa mostra só o que interessa.
   */
  const [disciplinasAlternadas, setDisciplinasAlternadas] = useState<ReadonlyMap<string, boolean>>(
    new Map(),
  );
  const idDaLista = useId();

  // O catálogo entra pela participação das bitolas (Fase 27, RN-378): o item de aço do
  // CORTE E DOBRA reparte o medido pela taxa da disciplina.
  const quantities: TakeoffQuantities = useMemo(
    () => quantitiesOf(draft, medidos, catalog, tipologia.pile, comodos, fundacao),
    [draft, medidos, catalog, tipologia.pile, comodos, fundacao],
  );
  /** O número de cada serviço nesta tipologia — traçado, corrigido ou digitado (fatia 5). */
  const medidosDosServicos = useMemo(
    () => quantidadesDosServicos(catalog, tipologia, draft, fundacao),
    [catalog, tipologia, draft, fundacao],
  );
  const procurado = busca.trim().toLocaleLowerCase('pt-BR');
  /** O aprovado não espera número de ninguém: ele não muda mais, e não é pendência. */
  const pendentesDe = useCallback(
    (servico: CatalogService): number =>
      aprovacaoDe(aprovados, servico.key) === undefined
        ? pendentesDoServico(servico, quantities)
        : 0,
    [aprovados, quantities],
  );

  const grupos = useMemo(
    () =>
      catalog.disciplines
        .map((disciplina) => {
          const servicos = disciplina.services.filter((servico) => {
            if (procurado === '') return true;
            const noServico = `${disciplina.name} ${servico.name}`
              .toLocaleLowerCase('pt-BR')
              .includes(procurado);
            const noItem = servico.items.some((item) =>
              `${item.code ?? ''} ${item.description}`
                .toLocaleLowerCase('pt-BR')
                .includes(procurado),
            );
            return noServico || noItem;
          });
          const itens = servicos.reduce((total, servico) => total + servico.items.length, 0);
          const lancados = servicos.reduce(
            (total, servico) => total + lancadosDoServico(servico, quantities),
            0,
          );
          const pendentes = servicos.reduce((total, servico) => total + pendentesDe(servico), 0);
          return { disciplina, servicos, itens, lancados, pendentes };
        })
        .filter((grupo) => grupo.servicos.length > 0),
    [catalog, procurado, quantities, pendentesDe],
  );

  /** Na ordem da lista: é por ela que "próximo pendente" anda. */
  const pendentes = useMemo(
    () =>
      grupos.flatMap((grupo) =>
        grupo.servicos
          .filter((servico) => pendentesDe(servico) > 0)
          .map((servico) => ({ disciplina: grupo.disciplina.key, servico: servico.key })),
      ),
    [grupos, pendentesDe],
  );

  const disciplinaAberta = (chave: string, pendentesDoGrupo: number): boolean =>
    // Com busca, tudo o que corresponde fica à vista: esconder o achado seria não achar.
    procurado !== '' || (disciplinasAlternadas.get(chave) ?? pendentesDoGrupo > 0);

  const alternarDisciplina = (chave: string, aberta: boolean) =>
    setDisciplinasAlternadas((atual) => new Map(atual).set(chave, !aberta));

  const alternar = (chave: string): void =>
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });

  /**
   * Abre o próximo serviço que espera número, depois do último aberto, e leva a tela até
   * ele. Quem lança não precisa percorrer oitenta linhas atrás do que falta.
   */
  const irParaProximoPendente = () => {
    if (pendentes.length === 0) return;
    const ultimoAberto = pendentes.findLastIndex((pendente) => abertos.has(pendente.servico));
    const proximo = pendentes[(ultimoAberto + 1) % pendentes.length]!;
    setDisciplinasAlternadas((atual) => new Map(atual).set(proximo.disciplina, true));
    setAbertos((atual) => new Set(atual).add(proximo.servico));
    requestAnimationFrame(() => {
      document
        .getElementById(domId(proximo.servico))
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };

  /**
   * Onde o código informado procura a quem propagar (RN-396): o catálogo da obra sem os serviços
   * aprovados, cujos itens não recebem código (Fase 33, RN-438).
   */
  const catalogoParaOCodigo = useMemo(
    () => semOsAprovados(catalogoDaObra, aprovados),
    [catalogoDaObra, aprovados],
  );

  /** Houve importação? É o que decide se faz sentido falar de "aba de referência". */
  const temPlanilha = hasImportedSheets(catalog);

  /**
   * O código conferido no compras acabou de ser aplicado: tira a foto do preço dele já (D5),
   * para o material nascer com o médio em vez de esperar o próximo "Atualizar preços".
   *
   * Não segura a troca do código — o despacho já aconteceu —, e a falha é só um aviso: o
   * código vale do mesmo jeito, e o "Atualizar preços" tenta de novo. Sem integração não se
   * diz nada: não há foto a tirar, e o orçamento segue no provisório (D8).
   */
  const atualizarPreco = precos?.atualizar;
  const periodoDosPrecos = precos?.periodo;
  const podeEditarPrecos = precos?.podeEditar === true;
  const tirarFotoDoCodigo = useCallback(
    (code: string): void => {
      if (atualizarPreco === undefined || periodoDosPrecos === undefined) return;
      void atualizarPreco([code], periodoDosPrecos).then((feito) => {
        if (feito?.erro !== undefined) {
          toast.warning(`O preço de ${code} não foi buscado agora: ${feito.erro}`);
        }
      });
    },
    [atualizarPreco, periodoDosPrecos],
  );

  /**
   * Catálogo importado antes de o item ter quantidade.
   *
   * Sem este aviso, a tela diria "0 de 8 itens" em noventa e dois serviços e deixaria a
   * pessoa procurando o próprio erro — a aba está escolhida, os itens estão lá, e mesmo
   * assim não há número. A explicação é que ele nunca foi guardado, e a saída é
   * reimportar o mesmo arquivo.
   */
  const catalogoSemQuantidadeDeItem =
    draft.sheetName !== undefined &&
    catalog.disciplines.every((disciplina) =>
      disciplina.services.every((servico) =>
        servico.items.every((item) => Object.keys(item.quantityBySheet).length === 0),
      ),
    ) &&
    catalog.disciplines.some((disciplina) =>
      disciplina.services.some((servico) => servico.items.length > 0),
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Itens por serviço</CardTitle>
        <CardDescription>
          {temPlanilha ? (
            <>
              O campo nasce com o que a aba{' '}
              {draft.sheetName === undefined ? (
                <strong className="font-medium text-(--color-state-warn)">
                  (nenhuma aba escolhida)
                </strong>
              ) : (
                <strong className="font-medium text-(--color-content)">“{draft.sheetName}”</strong>
              )}{' '}
              orçou; digitar por cima é discordar dela.
            </>
          ) : (
            'A quantidade de cada material, dentro do serviço que o consome.'
          )}
        </CardDescription>
        <ComoFunciona>
          {temPlanilha ? (
            <>
              Apagar o campo é voltar a seguir a planilha; zerar é decidir que o item não acontece
              aqui. O <strong>editado</strong> ao lado do número marca o que já foi revisado. Só
              aparecem os serviços <strong>escolhidos</strong> para esta tipologia, das disciplinas
              que os andares dela têm na Estrutura.
            </>
          ) : (
            <>
              Só aparecem os serviços <strong>escolhidos</strong> para esta tipologia, das
              disciplinas que os andares dela têm na Estrutura. Abra o serviço e acrescente o que
              ele consome; a planilha do orçamento, na montagem, preenche tudo de uma vez.
            </>
          )}
        </ComoFunciona>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-56 flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <Input
              aria-label="Procurar serviço ou material"
              placeholder="Procurar serviço ou material…"
              className="pl-9"
              value={busca}
              onChange={(event) => setBusca(event.target.value)}
            />
          </div>
          <Button
            variant="secondary"
            disabled={pendentes.length === 0}
            onClick={irParaProximoPendente}
          >
            <ArrowDownToLine aria-hidden="true" />
            {pendentes.length === 0
              ? 'Nada pendente'
              : `Próximo pendente · ${String(pendentes.length)}`}
          </Button>
        </div>

        {temPlanilha && draft.sheetName === undefined && (
          <p className="rounded-md border border-(--color-state-warn) px-3 py-2 text-sm">
            Esta tipologia não aponta nenhuma aba da planilha. Os campos nascem vazios — escolha a
            aba de referência em <strong>Serviços × traçado</strong>, na montagem, para partir dos
            números que a construtora já orçou.
          </p>
        )}

        {catalogoSemQuantidadeDeItem && (
          <p className="rounded-md border border-(--color-state-warn) px-3 py-2 text-sm">
            Esta planilha foi importada por uma versão do sistema que ainda não guardava a
            quantidade de cada <strong>item</strong> — só a do serviço. Por isso todos aparecem sem
            lançamento. <strong>Reimporte o mesmo arquivo</strong> na tela de montagem para trazer
            os números; o que já foi decidido aqui é preservado.
          </p>
        )}

        {grupos.length === 0 ? (
          <EmptyState
            title={procurado !== '' ? 'Nada encontrado' : 'Nada neste filtro'}
            description={
              procurado !== ''
                ? 'Nenhum serviço ou material escolhido nesta tipologia corresponde à busca.'
                : filtrado
                  ? 'Nenhum serviço escolhido nesta tipologia está na disciplina ou no serviço do filtro.'
                  : 'Nenhum serviço escolhido nesta tipologia.'
            }
            {...(filtrado && onLimparFiltro !== undefined && procurado === ''
              ? { action: { label: 'Limpar o filtro', onClick: onLimparFiltro } }
              : {})}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {grupos.map(
              ({ disciplina, servicos, itens, lancados, pendentes: pendentesDoGrupo }, indice) => {
                const aberta = disciplinaAberta(disciplina.key, pendentesDoGrupo);
                return (
                  <GrupoDaDisciplina
                    key={disciplina.key}
                    id={`${idDaLista}-${String(indice)}`}
                    disciplina={disciplina}
                    servicos={servicos}
                    itens={itens}
                    lancados={lancados}
                    pendentes={pendentesDoGrupo}
                    aberta={aberta}
                    onAlternar={() => alternarDisciplina(disciplina.key, aberta)}
                    // A taxa de aço mora na disciplina (ADR-176), e só faz sentido onde se
                    // compra aço — a que tem CORTE E DOBRA — ou onde algum serviço mede o aço
                    // da peça e precisa dela para ter número (Fase 27). A estaca não: o aço dela
                    // vem do tipo (etapa 1b), e a taxa por m³ ali só confundiria.
                    taxas={
                      !disciplinaDaEstaca(disciplina.name) &&
                      disciplina.services.some(
                        (servico) =>
                          isSteelPurchaseService(servico.name) || servico.measureBy === 'ACO',
                      ) ? (
                        <TaxasDeAco
                          disciplina={disciplina}
                          dispatch={dispatch}
                          travadaPor={acoAprovadoDa(aprovados, disciplina)}
                          {...(mediaDoApartamento === undefined ? {} : { mediaDoApartamento })}
                        />
                      ) : undefined
                    }
                  >
                    {servicos.map((servico) => (
                      <BlocoDoServico
                        key={servico.key}
                        servico={servico}
                        {...taxasDoServico(servico, taxaDoServico(disciplina, servico), fundacao)}
                        temPlanilha={temPlanilha}
                        tipologia={tipologia}
                        quantities={quantities}
                        {...(medidosDosServicos.get(servico.key) === undefined
                          ? {}
                          : { medido: medidosDosServicos.get(servico.key)! })}
                        aberto={abertos.has(servico.key)}
                        onAlternar={() => alternar(servico.key)}
                        foraDoAndar={foraDoAndar.has(servico.key)}
                        {...(onAbrirEstrutura === undefined ? {} : { onAbrirEstrutura })}
                        funcoes={funcoes.doServico(servico.key)}
                        nomeDaFuncao={funcoes.nome}
                        idDoServico={funcoes.idDoServico(servico.key)}
                        catalogoDaObra={catalogoParaOCodigo}
                        dispatch={dispatch}
                        {...(precos === undefined ? {} : { precos })}
                        {...(custo === undefined ? {} : { custo })}
                        {...(podeEditarPrecos ? { onCodigoConferido: tirarFotoDoCodigo } : {})}
                        {...(aprovacaoDe(aprovados, servico.key) === undefined
                          ? {}
                          : { aprovado: aprovacaoDe(aprovados, servico.key)! })}
                        nomesDosTipos={nomesDosTipos}
                        {...(tiposDeEstaca === undefined ? {} : { tiposDeEstaca })}
                        {...(mediaDoApartamento === undefined ? {} : { mediaDoApartamento })}
                        comodos={draft.rooms}
                      />
                    ))}
                  </GrupoDaDisciplina>
                );
              },
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Uma disciplina ──────────────────────────────────────────────────────────────

/**
 * O título da disciplina é também o botão que a recolhe.
 *
 * Diz, fechado, o que decide se vale abrir: quantos serviços, quantos itens já têm número
 * e quantos esperam. Recolher não esconde a pendência — o número dela fica no título.
 */
function GrupoDaDisciplina({
  id,
  disciplina,
  servicos,
  itens,
  lancados,
  pendentes,
  aberta,
  onAlternar,
  taxas,
  children,
}: {
  id: string;
  disciplina: CatalogDiscipline;
  servicos: readonly CatalogService[];
  itens: number;
  lancados: number;
  pendentes: number;
  aberta: boolean;
  onAlternar: () => void;
  /** O editor das taxas de aço, na disciplina que compra aço (Fase 27). */
  taxas?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="flex flex-col gap-1">
      <h3 id={id} className="m-0">
        <button
          type="button"
          onClick={onAlternar}
          aria-expanded={aberta}
          className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-(--color-surface-muted)"
        >
          {aberta ? (
            <ChevronDown
              className="size-4 shrink-0 text-(--color-content-muted)"
              aria-hidden="true"
            />
          ) : (
            <ChevronRight
              className="size-4 shrink-0 text-(--color-content-muted)"
              aria-hidden="true"
            />
          )}
          <span className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-(--color-content-muted)">
            {disciplina.name}
          </span>
          <span className="h-px flex-1 bg-(--color-border-subtle)" aria-hidden="true" />
          <span className="tabular shrink-0 text-xs text-(--color-content-muted)">
            {servicos.length} {servicos.length === 1 ? 'serviço' : 'serviços'} ·{' '}
            {itens === 0
              ? 'nenhum item ainda'
              : `${String(lancados)} de ${String(itens)} ${itens === 1 ? 'item lançado' : 'itens lançados'}`}
          </span>
          {pendentes > 0 ? (
            <span className="tabular shrink-0 rounded-full border border-(--color-state-warn) px-2 text-xs text-(--color-state-warn)">
              {pendentes} {pendentes === 1 ? 'pendente' : 'pendentes'}
            </span>
          ) : (
            <Check className="size-4 shrink-0 text-(--color-state-ok)" aria-label="completo" />
          )}
        </button>
      </h3>
      {aberta && taxas}
      {aberta && <ul className="flex flex-col md:pl-6">{children}</ul>}
    </section>
  );
}

// ── As taxas de aço da disciplina (Fase 27) ─────────────────────────────────────

/** A disciplina cuja peça é a estaca — ESTACAS (Fase 27, etapa 1b). */
function disciplinaDaEstaca(nome: string): boolean {
  const chave = normalizeName(nome);
  return STAGE_REGISTRY.some(
    (disciplina) => disciplina.element === 'ESTACA' && normalizeName(disciplina.name) === chave,
  );
}

/**
 * Os kg de cada bitola por m³ de concreto da peça — uma linha por bitola (RN-367).
 *
 * Mora na disciplina, e não no item (ADR-176): o CORTE E DOBRA, a MONTAGEM e a INSTALAÇÃO
 * leem o mesmo kg, e é a soma das bitolas que a medida "aço" multiplica pelo volume. A
 * participação de cada uma é o que reparte o kg entre os itens de aço do CORTE E DOBRA
 * (RN-378). Campo vazio ou zero é "esta disciplina não usa esta bitola", e não entra.
 *
 * Na disciplina cujos serviços de aço medem mais de um elemento (Fase 42, etapa 3 — RN-573), o
 * mesmo bloco mostra, embaixo da lista da disciplina, uma lista por elemento: na PILARES DE
 * CONTENÇÃO, a do muro e a do pilar. O elemento sem a própria lê a da disciplina, e a tela diz
 * "da disciplina"; mudar uma bitola nele cria a dele, a partir da da disciplina — como a taxa
 * própria da peça (Fase 41). A disciplina de um elemento só fica como sempre foi.
 */
function TaxasDeAco({
  disciplina,
  dispatch,
  travadaPor = [],
  mediaDoApartamento,
}: {
  disciplina: CatalogDiscipline;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** A média do aço da laje (Fase 40, etapa 3 — RN-533, RN-534): a origem e a média ao lado. */
  mediaDoApartamento?: ApartmentAverageForProject;
  /**
   * Os serviços de aço aprovados da disciplina (Fase 33, RN-438): com um deles, a taxa só se lê
   * — mudá-la mudaria a quantidade do aprovado, e o servidor recusaria. Vale para todas as
   * listas da disciplina, as dos elementos também (Fase 42, RN-575).
   */
  travadaPor?: readonly AprovacaoDoServico[];
}) {
  const travada = travadaPor.length > 0;
  const total = totalSteelRate(disciplina.steelRates);
  // A disciplina que mistura elementos (Fase 42, RN-573): a média vai para a lista de cada um.
  const elementos = elementosDaTaxa(disciplina);
  const media = mediaDoApartamento !== undefined && !travada ? mediaDoApartamento : undefined;
  // Na que mistura, falta taxa só quando algum elemento não lê nenhuma.
  const semTaxa =
    elementos.length === 0
      ? total === 0
      : elementos.some((elemento) => totalSteelRate(steelRatesFor(disciplina, elemento)) === 0);

  return (
    <div className="mb-2 rounded-md border border-(--color-border-subtle) px-3 py-2 md:ml-6">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-xs font-medium">Taxas de aço (kg/m³)</span>
        <span
          className={cn(
            'tabular text-xs',
            semTaxa ? 'text-(--color-state-warn)' : 'text-(--color-content-muted)',
          )}
        >
          {total > 0
            ? `${metros(total, 3)} kg/m³ somadas as bitolas`
            : semTaxa
              ? 'sem taxa de aço — o aço da peça não mede'
              : 'sem taxa na disciplina — cada elemento tem a dele'}
        </span>
      </div>
      <BitolasDaTaxa
        disciplina={disciplina}
        taxas={disciplina.steelRates}
        travada={travada}
        dispatch={dispatch}
        {...(elementos.length === 0 && media !== undefined ? { media } : {})}
      />
      {elementos.length > 0 && (
        <p className="mt-2 text-xs text-(--color-content-muted)">
          Os serviços de aço desta disciplina medem mais de um elemento: cada um lê a lista dele, e
          o que não tem a própria lê a da disciplina, acima.
        </p>
      )}
      {elementos.map((elemento) => (
        <TaxasDoElemento
          key={elemento}
          disciplina={disciplina}
          elemento={elemento}
          travada={travada}
          dispatch={dispatch}
          {...(media === undefined ? {} : { media })}
        />
      ))}
      {travada && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-(--color-content-muted)">
          <Lock className="mt-0.5 size-3.5 shrink-0 text-(--color-state-ok)" aria-hidden="true" />
          <span>
            A taxa não muda mais: {travadaPor.map(nomeDoAprovado).join(', ')}{' '}
            {travadaPor.length === 1 ? 'foi aprovado' : 'foram aprovados'} no orçamento, e{' '}
            {travadaPor.length === 1 ? 'lê' : 'leem'} esta taxa.
          </span>
        </p>
      )}
    </div>
  );
}

/**
 * A lista de um elemento na disciplina que mistura (Fase 42, RN-573): a própria, com "Voltar à da
 * disciplina"; sem ela, a da disciplina, marcada "da disciplina" — e mudar uma bitola cria a
 * própria, copiando a da disciplina inteira com a bitola trocada.
 */
function TaxasDoElemento({
  disciplina,
  elemento,
  travada,
  dispatch,
  media,
}: {
  disciplina: CatalogDiscipline;
  elemento: AppliesTo;
  travada: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  media?: ApartmentAverageForProject;
}) {
  const rotulo = APPLIES_TO_LABEL[elemento];
  const propria = temListaPropria(disciplina, elemento);
  const taxas = steelRatesFor(disciplina, elemento);
  const total = totalSteelRate(taxas);
  return (
    <section
      aria-label={`Taxas de aço · ${rotulo}`}
      className="mt-2 border-t border-(--color-border-subtle) pt-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-xs font-medium">
          {rotulo}{' '}
          <span className="font-normal text-(--color-content-muted)">
            {propria ? '(própria)' : '(da disciplina)'}
          </span>
        </span>
        <span
          className={cn(
            'tabular text-xs',
            total > 0 ? 'text-(--color-content-muted)' : 'text-(--color-state-warn)',
          )}
        >
          {total > 0
            ? `${metros(total, 3)} kg/m³ somadas as bitolas`
            : 'sem taxa de aço — o aço da peça não mede'}
        </span>
      </div>
      {!travada && (
        <p className="mt-1 text-xs text-(--color-content-muted)">
          {propria
            ? `A lista própria vale inteira para os serviços de aço de ${rotulo.toLocaleLowerCase('pt-BR')}, no lugar da da disciplina.`
            : `Vale a da disciplina. Mudar uma bitola cria a própria de ${rotulo.toLocaleLowerCase('pt-BR')}, a partir dela.`}
        </p>
      )}
      <BitolasDaTaxa
        disciplina={disciplina}
        elemento={elemento}
        taxas={taxas}
        travada={travada}
        dispatch={dispatch}
        {...(media === undefined ? {} : { media })}
      />
      {propria && !travada && (
        <div className="mt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              dispatch({
                type: 'SET_STEEL_RATES',
                disciplineKey: disciplina.key,
                element: elemento,
                rates: [],
              })
            }
          >
            <RotateCcw className="size-4" aria-hidden="true" /> Voltar à da disciplina
          </Button>
        </div>
      )}
    </section>
  );
}

/**
 * Uma lista de bitolas da taxa: a da disciplina inteira, ou — com `elemento` — a que ele lê (Fase
 * 42, RN-573). Com a média, a origem embaixo de cada bitola e o "A média mudou" da lista.
 *
 * A troca manda a lista inteira, a partir da que está na tela: na do elemento sem a própria, é a
 * da disciplina — e a do elemento nasce dela.
 */
function BitolasDaTaxa({
  disciplina,
  elemento,
  taxas: lista,
  travada,
  dispatch,
  media: mediaDoApartamento,
}: {
  disciplina: CatalogDiscipline;
  elemento?: AppliesTo;
  taxas: readonly SteelRate[] | undefined;
  travada: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
  media?: ApartmentAverageForProject;
}) {
  const taxas = new Map<SteelGauge, number>(
    (lista ?? []).map((taxa) => [taxa.gauge, taxa.kgPerM3]),
  );
  // O aço com média: o da laje (Fase 40) ou o da fundação, pelo elemento (Fase 41, RN-553).
  const elementoDaMedia = elementoDoAcoDaLista(disciplina, elemento);
  const media = elementoDaMedia === undefined ? undefined : mediaDoApartamento;
  const origens = new Map((lista ?? []).map((taxa) => [taxa.gauge, taxa]));
  const mudancas = media === undefined ? [] : mudancasDaTaxa(disciplina, media, elemento);
  const doElemento = elemento === undefined ? {} : { element: elemento };
  const onde =
    elemento === undefined ? disciplina.name : `${disciplina.name} · ${APPLIES_TO_LABEL[elemento]}`;

  const trocar = (gauge: SteelGauge, valor: number | undefined) => {
    const proximas: SteelRate[] = STEEL_GAUGES.flatMap((info) => {
      const kg = info.code === gauge ? valor : taxas.get(info.code);
      // Zero não é taxa; o negativo segue, e o rascunho o recusa dizendo por quê.
      return kg === undefined || kg === 0 ? [] : [{ gauge: info.code, kgPerM3: kg }];
    });
    dispatch({
      type: 'SET_STEEL_RATES',
      disciplineKey: disciplina.key,
      rates: proximas,
      ...doElemento,
    });
  };

  return (
    <>
      <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-5">
        {STEEL_GAUGES.map((info) => (
          <label key={info.code} className="flex items-center justify-between gap-2 text-xs">
            <span className="text-(--color-content-muted)">{info.label}</span>
            {travada ? (
              <span className="tabular h-8 w-20 py-1.5 text-right">
                {taxas.get(info.code) === undefined ? '—' : metros(taxas.get(info.code)!, 3)}
              </span>
            ) : (
              <span className="flex flex-col items-end">
                <NumeroOpcionalInput
                  aria-label={`Taxa de ${info.label} em ${onde}, em kg por m³`}
                  placeholder="—"
                  className="h-8 w-20 text-right"
                  value={taxas.get(info.code)}
                  onChange={(valor) => trocar(info.code, valor)}
                />
                {media !== undefined && (
                  <OrigemDaTaxa
                    rotulo={
                      elemento === undefined
                        ? info.label
                        : `${info.label} · ${APPLIES_TO_LABEL[elemento]}`
                    }
                    taxa={origens.get(info.code)}
                    hoje={mediaDaBitola(info.code, media, elementoDaMedia)}
                    onUsar={(hoje) =>
                      dispatch({
                        type: 'APPLY_STEEL_AVERAGE',
                        disciplineKey: disciplina.key,
                        gauge: info.code,
                        average: fotoDaTaxa(hoje, media.at),
                        follow: true,
                        ...doElemento,
                      })
                    }
                  />
                )}
              </span>
            )}
          </label>
        ))}
      </div>
      {media !== undefined && mudancas.length > 0 && (
        <section
          aria-label={
            elemento === undefined
              ? 'A média da taxa mudou'
              : `A média da taxa mudou · ${APPLIES_TO_LABEL[elemento]}`
          }
          className="mt-2 flex flex-col gap-2 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) px-3 py-2 text-xs"
        >
          <p className="font-medium">
            A média mudou
            {mudancas[0]?.desde === undefined ? '' : ` desde ${diaEMes(mudancas[0].desde)}`}
          </p>
          <ul className="flex flex-col gap-0.5">
            {mudancas.map((mudanca) => (
              <li key={mudanca.gauge} className="tabular">
                {steelGaugeLabel(mudanca.gauge)}: {metros(mudanca.de, 3)} →{' '}
                {metros(mudanca.para, 3)} kg/m³
              </li>
            ))}
          </ul>
          <p className="text-(--color-content-muted)">
            Atualizar troca só a taxa que segue a média; a digitada fica, com a média nova ao lado.
          </p>
          <div>
            <Button
              size="sm"
              variant="secondary"
              onClick={() =>
                atualizarTaxaPelaMedia(disciplina, media, elemento).forEach((acao) =>
                  dispatch(acao),
                )
              }
            >
              Atualizar pela média
            </Button>
          </div>
        </section>
      )}
    </>
  );
}

// ── Um serviço ──────────────────────────────────────────────────────────────────

/**
 * A taxa que a medida "aço" do serviço multiplica: a da disciplina, ou — na fundação com taxa por
 * peça (Fase 41, etapa 2 — RN-545) — a equivalente das peças desta tipologia.
 */
function taxasDoServico(
  servico: CatalogService,
  daDisciplina: readonly SteelRate[] | undefined,
  fundacao: FoundationFactors | undefined,
): { readonly taxasDeAco?: readonly SteelRate[] } {
  const taxas = foundationSteelRates(servico, fundacao, daDisciplina) ?? daDisciplina;
  return taxas === undefined ? {} : { taxasDeAco: taxas };
}

/**
 * Uma linha por serviço, separada por fio — e não uma caixa por serviço.
 *
 * Recolhida, diz o que interessa para decidir se vale abrir: como ele mede, o que a
 * planilha orçou, o que o desenho determina, e quantos itens já têm quantidade. Aberta,
 * mostra a tabela de itens e o campo de acrescentar.
 *
 * O "pelo desenho" só aparece quando o desenho respondeu algo: zero em toda linha de uma
 * tipologia sem ambiente diria que o desenho mediu "nada", e ele não mediu — ainda não
 * existe.
 */
function BlocoDoServico({
  servico,
  taxasDeAco,
  temPlanilha,
  tipologia,
  quantities,
  medido,
  aberto,
  onAlternar,
  foraDoAndar,
  onAbrirEstrutura,
  funcoes,
  nomeDaFuncao,
  idDoServico,
  catalogoDaObra,
  dispatch,
  precos,
  custo,
  onCodigoConferido,
  aprovado,
  nomesDosTipos,
  tiposDeEstaca,
  mediaDoApartamento,
  comodos = [],
}: {
  servico: CatalogService;
  /** As taxas de aço da disciplina do serviço — o que a medida "aço" multiplica (Fase 27). */
  taxasDeAco?: readonly SteelRate[];
  /** Sem planilha não há "o que a aba orçou": o número lançado é o único que existe. */
  temPlanilha: boolean;
  tipologia: GeometryTotals;
  quantities: TakeoffQuantities;
  /**
   * O número do serviço nesta tipologia (fatia 5), com a parte que veio pronta das peças
   * importadas ao lado (Fase 28) — o índice multiplica só a outra (`baseDoIndice`).
   */
  medido?: ServicoComPecas;
  aberto: boolean;
  onAlternar: () => void;
  /** A disciplina saiu dos andares da tipologia, mas o serviço tem número e fica. */
  foraDoAndar: boolean;
  onAbrirEstrutura?: () => void;
  /** As funções que executam a disciplina deste serviço. */
  funcoes: readonly FuncaoDoServico[];
  nomeDaFuncao: (jobRoleId: string) => string | undefined;
  /** O id gravado do serviço — o que a sugestão de produtividade pergunta (fatia 7). */
  idDoServico: ServiceId | undefined;
  /** Onde o código informado procura os itens de mesma descrição (Fase 29, RN-396). */
  catalogoDaObra: ServiceCatalog;
  dispatch: React.Dispatch<WorkspaceAction>;
  /** O preço de material da obra — as duas colunas só de leitura (Fase 29, etapa 2). */
  precos?: PrecosDaObraNaTela;
  /** O R$/h da mão de obra, nas mesmas colunas, só de leitura (Fase 29, etapa 3). */
  custo?: CustoDaMaoDeObraNaTela;
  /** O código conferido foi aplicado: a foto do preço dele (D5). */
  onCodigoConferido?: (code: string) => void;
  /**
   * O serviço foi aprovado no orçamento (Fase 33, RN-438): o cadeado no título, e a tabela só
   * para ler — nada de item novo, remover, índice, perda, código, classe, função ou quantidade.
   */
  aprovado?: AprovacaoDoServico;
  /** O nome de cada tipo de estaca, pelo id (Fase 39). */
  nomesDosTipos: ReadonlyMap<string, string>;
  /** O botão dos Tipos de estaca, no serviço que mede a estaca por tipo (Fase 39). */
  tiposDeEstaca?: React.ReactNode;
  /** A média do apartamento da obra (Fase 40). */
  mediaDoApartamento?: ApartmentAverageForProject;
  /** Os cômodos da tipologia — a média do tipo mora no próprio de cada um (Fase 40, etapa 2). */
  comodos?: TypologyDraft['rooms'];
}) {
  const travado = aprovado !== undefined;
  // O que segue a média e mudou (RN-522): no serviço aberto, e nunca no aprovado (Fase 33).
  const mudancas =
    mediaDoApartamento === undefined || travado
      ? []
      : mudancasDoServico(servico, mediaDoApartamento);
  // E o próprio dos cômodos que segue a média do tipo e mudou (etapa 2, RN-529).
  const mudancasDoTipo =
    mediaDoApartamento === undefined || travado
      ? []
      : mudancasDosComodos(servico, comodos, mediaDoApartamento);
  const lancados = lancadosDoServico(servico, quantities);
  // Na estaca, o índice é por tipo (Fase 39, RN-508): a parte de cada tipo desenhado na medida.
  const partesDosTipos = quantities.pileShares?.[servico.key];
  const total = servico.items.length;
  const completo = total > 0 && lancados === total;

  const doServico =
    quantities.sheetName === undefined ? undefined : servico.quantityBySheet[quantities.sheetName];

  /**
   * Quanto deste serviço esta tipologia tem, pelo desenho.
   *
   * É a base do lançamento: quantos sacos de argamassa para **estes** 121,80 m² de
   * alvenaria. Sem o número à vista, quem lança o material tinha de decorá-lo da outra
   * aba — ou lançar no escuro.
   */
  const comTaxa = withSteelRate(servico, taxasDeAco);
  const traced = totalFor(comTaxa, tipologia);
  const desenhou = traced !== undefined && traced > 0;
  /**
   * O aço da peça sem taxa na disciplina (Fase 27): o desenho tem o volume, mas ninguém disse
   * quantos kg vão em cada m³ — a lista diz isso, em vez de calar como se não houvesse peça.
   */
  const semTaxaDeAco =
    servico.measureBy === 'ACO' &&
    !isPendingElement(servico) &&
    // O aço da estaca vem do tipo (etapa 1b): taxa da disciplina não é o que falta ali.
    serviceElement(servico) !== 'ESTACA' &&
    comTaxa.steelRatePerM3 === undefined;
  // A espessura só vira volume sobre uma área: contagem e comprimento não têm camada.
  const volume =
    traced === undefined || !isAreaMeasure(servico.measureBy)
      ? undefined
      : serviceVolume(servico, traced);
  /**
   * A unidade em que o índice multiplica — a do medido (fatia 6).
   *
   * No serviço do desenho é a da medida (m², m³); no digitado, o número já é da unidade do
   * serviço, e a da planilha é a que se tem à mão aqui.
   */
  const unidadeDoMedido =
    medido?.measureUnitCode === undefined
      ? (servico.unit ?? 'un')
      : unitLabel(medido.measureUnitCode);

  const detalhes = [
    medidaCurta(servico),
    doServico === undefined
      ? undefined
      : `planilha ${metros(doServico)} ${servico.unit ?? ''}`.trim(),
    // O medido que o índice multiplica: o traçado, ou o número que a tipologia decidiu.
    medido?.measuredOrigin === 'CORRIGIDO' || medido?.measuredOrigin === 'DIGITADO'
      ? `${numeroDaMedida(medido.measured!, servico.measureBy)} ${unidadeDoMedido} ${
          medido.measuredOrigin === 'CORRIGIDO' ? 'medido (corrigido)' : 'digitado'
        }`
      : desenhou
        ? [
            numeroDaMedida(traced, servico.measureBy),
            measureUnit(serviceElement(servico), servico.measureBy),
            'pelo desenho',
          ]
            .filter((parte) => parte !== '')
            .join(' ')
        : undefined,
    desenhou && volume !== undefined && volume > 0 ? `${metros(volume, 3)} m³` : undefined,
    // A parte que veio pronta das peças importadas (Fase 28, RN-388): soma-se ao desenho, que
    // já não mede essas peças — e o número digitado, quando há, vence as duas.
    medido?.fromPieces === undefined
      ? undefined
      : `${metros(medido.fromPieces)} ${servico.unit ?? unidadeDoMedido} da ${ROTULO_DAS_PECAS}`,
    semTaxaDeAco && medido?.measuredOrigin !== 'CORRIGIDO' ? 'sem taxa de aço' : undefined,
  ].filter((parte) => parte !== undefined);

  return (
    <li
      data-servico
      id={domId(servico.key)}
      className="scroll-mt-40 border-b border-(--color-border-subtle) last:border-0"
    >
      <button
        type="button"
        onClick={onAlternar}
        aria-expanded={aberto}
        className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-2 rounded-md px-2 py-2 text-left hover:bg-(--color-surface-muted)"
      >
        {aberto ? (
          <ChevronDown
            className="size-4 shrink-0 text-(--color-content-muted)"
            aria-hidden="true"
          />
        ) : (
          <ChevronRight
            className="size-4 shrink-0 text-(--color-content-muted)"
            aria-hidden="true"
          />
        )}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{servico.name}</span>
          <span className="block truncate text-xs text-(--color-content-muted)">
            {detalhes.join(' · ')}
          </span>
        </span>
        {aprovado !== undefined && (
          <span
            className="flex shrink-0 items-center gap-1 text-xs font-medium text-(--color-state-ok)"
            title={`Serviço ${textoDaAprovacao(aprovado)}: a quantidade não muda mais.`}
          >
            <Lock className="size-3.5" aria-hidden="true" />
            aprovado
          </span>
        )}
        <span
          className={cn(
            'tabular flex shrink-0 items-center gap-1 text-xs',
            total === 0
              ? 'text-(--color-content-muted)'
              : completo
                ? 'text-(--color-state-ok)'
                : 'text-(--color-state-warn)',
          )}
        >
          {completo && <Check className="size-3.5" aria-hidden="true" />}
          {total === 0
            ? 'sem item'
            : completo
              ? `${String(total)} ${total === 1 ? 'item' : 'itens'}`
              : `${String(lancados)} de ${String(total)} ${total === 1 ? 'item' : 'itens'}`}
        </span>
      </button>

      {foraDoAndar && (
        <p className="mb-2 flex flex-wrap items-center gap-x-2 rounded-md border border-(--color-state-warn) px-3 py-1.5 text-xs">
          <span>
            A disciplina deste serviço não está cadastrada nos andares desta tipologia. Ele continua
            aqui porque tem quantidade.
          </span>
          {onAbrirEstrutura !== undefined && (
            <button
              type="button"
              className="font-medium text-(--color-brand) underline-offset-4 hover:underline"
              onClick={onAbrirEstrutura}
            >
              Abrir a Estrutura
            </button>
          )}
        </p>
      )}

      {aberto && (
        <div className="flex flex-col gap-3 pb-4 pt-1 md:pl-6">
          {aprovado !== undefined && (
            <p className="flex items-start gap-2 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) px-3 py-2 text-xs">
              <Lock
                className="mt-0.5 size-3.5 shrink-0 text-(--color-state-ok)"
                aria-hidden="true"
              />
              <span>
                Serviço {textoDaAprovacao(aprovado)}. Os itens, o índice, a perda, o código e a
                quantidade não mudam mais: serviço aprovado não reabre.
              </span>
            </p>
          )}
          {mudancas.length + mudancasDoTipo.length > 0 && mediaDoApartamento !== undefined && (
            <section
              aria-label="A média mudou"
              className="flex flex-col gap-2 rounded-md border border-(--color-state-warn) bg-(--color-surface-muted) px-3 py-2 text-xs"
            >
              <p className="font-medium">
                A média mudou
                {mudancas[0]?.desde === undefined ? '' : ` desde ${diaEMes(mudancas[0].desde)}`}
              </p>
              <ul className="flex flex-col gap-0.5">
                {mudancas.map((mudanca) => (
                  <li key={mudanca.itemKey} className="tabular">
                    {mudanca.descricao}: {metros(mudanca.de, 4)} → {metros(mudanca.para, 4)}
                  </li>
                ))}
                {mudancasDoTipo.map((mudanca) => (
                  <li key={`${mudanca.roomKind}|${mudanca.itemKey}`} className="tabular">
                    {ROOM_KIND_LABEL[mudanca.roomKind]} · {mudanca.descricao}:{' '}
                    {metros(mudanca.de, 4)} → {metros(mudanca.para, 4)}
                  </li>
                ))}
              </ul>
              <p className="text-(--color-content-muted)">
                Atualizar troca só o que segue a média; o índice digitado fica, com a média nova ao
                lado.
              </p>
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    [
                      ...atualizarServicoPelaMedia(servico, mediaDoApartamento),
                      ...atualizarComodosPelaMedia(servico, comodos, mediaDoApartamento),
                    ].forEach((acao) => dispatch(acao))
                  }
                >
                  Atualizar pela média
                </Button>
              </div>
            </section>
          )}
          {partesDosTipos !== undefined && (
            <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-(--color-content-muted)">
              <span>
                Na estaca, o índice é por tipo: cada diâmetro tem o fator dele. O tipo sem fator
                para o item usa o índice da obra.
              </span>
              {tiposDeEstaca}
            </p>
          )}
          {servico.items.length === 0 ? (
            <p className="text-sm text-(--color-content-muted)">
              {travado
                ? 'Nenhum material neste serviço.'
                : 'Nenhum material neste serviço ainda. Acrescente abaixo o que a obra usa.'}
            </p>
          ) : (
            <TableWrapper className="max-h-[28rem] overflow-y-auto rounded-md border border-(--color-border-subtle)">
              <Table>
                <TableHeader sticky>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead className="w-36">Classe</TableHead>
                    <TableHead className="w-16">Un.</TableHead>
                    {temPlanilha && <TableHead className="w-28 text-right">Planilha</TableHead>}
                    {/*
                      A conversão (Fase 24, fatia 6): quanto do item por unidade do medido,
                      a perda, e o que a conta dá — medido × índice × (1 + perda).
                    */}
                    <TableHead className="w-32">Índice</TableHead>
                    <TableHead className="w-24">Perda %</TableHead>
                    <TableHead className="w-28 text-right">Calculado</TableHead>
                    <TableHead className="w-40">Quantidade</TableHead>
                    {/*
                      O preço é do material na obra (D7), e não da linha: aqui ele só se lê, e a
                      conta é a quantidade desta linha vezes ele. A tabela já é larga — no
                      celular as duas colunas saem, e o preço continua na consolidação.
                    */}
                    {precos !== undefined && (
                      <>
                        <TableHead className="hidden w-36 text-right md:table-cell">
                          Preço unit.
                        </TableHead>
                        <TableHead className="hidden w-32 text-right md:table-cell">
                          Total
                        </TableHead>
                      </>
                    )}
                    <TableHead className="w-20">
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {servico.items.map((item) => {
                    const resolvida = resolveItemQuantity(item, servico.key, quantities);
                    const editada = resolvida.origin === 'MANUAL';
                    const pendente = resolvida.quantity === undefined;

                    return (
                      <TableRow
                        key={item.key}
                        data-pendente={pendente || undefined}
                        className={cn(
                          pendente &&
                            'bg-(--color-surface-muted) shadow-[inset_3px_0_0_var(--color-state-warn)]',
                        )}
                      >
                        <TableCell>
                          <span className="block max-w-72 truncate font-medium">
                            {item.description}
                          </span>
                          {item.kind === 'MAO_DE_OBRA' && travado ? (
                            <span className="mt-1 block text-xs text-(--color-content-muted)">
                              {item.jobRoleId === undefined
                                ? 'sem função'
                                : (nomeDaFuncao(item.jobRoleId) ?? 'função')}
                            </span>
                          ) : item.kind === 'MAO_DE_OBRA' ? (
                            <>
                              <FuncaoDoItem
                                item={item}
                                funcoes={funcoes}
                                nomeDaFuncao={nomeDaFuncao}
                                onEscolher={(jobRoleId) =>
                                  dispatch({
                                    type: 'SET_ITEM_JOB_ROLE',
                                    serviceKey: servico.key,
                                    itemKey: item.key,
                                    ...(jobRoleId === undefined ? {} : { jobRoleId }),
                                  })
                                }
                              />
                              {/*
                                A média que a obra já deu para esta função (fatia 7). Só depois
                                de escolhida a função, e só vira índice no "Usar": o item é
                                gravado pelo mesmo caminho de qualquer índice digitado.
                              */}
                              {item.jobRoleId !== undefined && (
                                <SugestaoDeProdutividade
                                  serviceId={idDoServico}
                                  jobRoleId={item.jobRoleId}
                                  description={item.description}
                                  indiceAtual={item.consumptionIndex}
                                  onUsar={(consumptionIndex) =>
                                    dispatch({
                                      type: 'SET_ITEM_INDEX',
                                      serviceKey: servico.key,
                                      itemKey: item.key,
                                      consumptionIndex,
                                    })
                                  }
                                />
                              )}
                            </>
                          ) : (
                            // O código ao lado de quem o troca (Fase 29, RN-395): o item que
                            // veio da planilha sem código ganha um aqui, sem ser recadastrado.
                            <span className="flex flex-wrap items-baseline gap-x-2">
                              <span className="font-mono text-xs text-(--color-content-muted)">
                                {item.code ?? 'sem código de compras'}
                              </span>
                              {!travado && (
                                <CodigoDoItem
                                  item={item}
                                  servico={servico}
                                  catalogoDaObra={catalogoDaObra}
                                  dispatch={dispatch}
                                  {...(onCodigoConferido === undefined
                                    ? {}
                                    : { onCodigoConferido })}
                                />
                              )}
                            </span>
                          )}
                          {/* O aço do CORTE E DOBRA diz a bitola (Fase 27, RN-366). */}
                          {item.steelGauge !== undefined && (
                            <span className="block text-xs text-(--color-content-muted)">
                              aço {steelGaugeLabel(item.steelGauge)}
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {travado ? (
                            <span className="text-xs">{ITEM_KIND_LABEL[item.kind]}</span>
                          ) : (
                            <select
                              className={`${SELECT_CLASS} w-full min-w-32`}
                              aria-label={`Classe de ${item.description}`}
                              value={item.kind}
                              onChange={(event) =>
                                dispatch({
                                  type: 'SET_ITEM_KIND',
                                  serviceKey: servico.key,
                                  itemKey: item.key,
                                  kind: event.target.value as ItemKind,
                                })
                              }
                            >
                              {ITEM_KINDS.map((kind) => (
                                <option key={kind} value={kind}>
                                  {ITEM_KIND_LABEL[kind]}
                                </option>
                              ))}
                            </select>
                          )}
                          {!travado && item.kindOrigin === 'PALPITE' && (
                            <span className="mt-0.5 block text-xs text-(--color-content-muted)">
                              palpite
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {item.kind === 'MAO_DE_OBRA' ? 'HH' : (item.unit ?? '—')}
                        </TableCell>
                        {temPlanilha && (
                          <TableCell className="tabular text-right text-(--color-content-muted)">
                            {resolvida.fromSheet === undefined
                              ? '—'
                              : metros(resolvida.fromSheet, 3)}
                          </TableCell>
                        )}
                        <ConversaoDoItem
                          item={item}
                          servico={servico}
                          unidadeDoMedido={unidadeDoMedido}
                          calculado={resolvida.calculated}
                          {...(item.steelGauge === undefined
                            ? {}
                            : {
                                participacao:
                                  quantities.gaugeShares?.[servico.key]?.[item.steelGauge],
                              })}
                          travado={travado}
                          porTipo={
                            item.steelGauge === undefined
                              ? fatoresDoItemPorTipo(
                                  servico.key,
                                  item.key,
                                  partesDosTipos,
                                  quantities.pile,
                                  nomesDosTipos,
                                )
                              : []
                          }
                          {...(mediaDoApartamento === undefined || item.steelGauge !== undefined
                            ? {}
                            : {
                                mediaHoje: (() => {
                                  const hoje = mediaDoItem(item, servico, mediaDoApartamento);
                                  return hoje === undefined
                                    ? undefined
                                    : fotoDaMedia(hoje, mediaDoApartamento.at);
                                })(),
                              })}
                          dispatch={dispatch}
                        />
                        <TableCell>
                          {/*
                            A quantidade que veio pronta das peças (Fase 28) mostra-se como a
                            calculada: um número que ninguém digitou, e discordar dele pede motivo.
                          */}
                          {travado ? (
                            <QuantidadeTravada resolvida={resolvida} />
                          ) : resolvida.calculated === undefined &&
                            resolvida.fromPieces === undefined ? (
                            <div className="flex items-center gap-2">
                              <NumeroOpcionalInput
                                aria-label={`Quantidade de ${item.description} em ${servico.name}`}
                                placeholder={temPlanilha ? 'segue a planilha' : 'quantidade'}
                                className="h-9 w-28 text-right"
                                value={resolvida.quantity}
                                onChange={(quantity) =>
                                  dispatch(
                                    quantity === undefined
                                      ? {
                                          type: 'CLEAR_ITEM_QUANTITY',
                                          serviceKey: servico.key,
                                          itemKey: item.key,
                                        }
                                      : {
                                          type: 'SET_ITEM_QUANTITY',
                                          serviceKey: servico.key,
                                          itemKey: item.key,
                                          quantity,
                                        },
                                  )
                                }
                              />
                              {temPlanilha && editada && (
                                <span className="text-xs text-(--color-state-info)">editado</span>
                              )}
                              {temPlanilha && !editada && resolvida.quantity !== undefined && (
                                <span className="text-xs text-(--color-content-muted)">
                                  planilha
                                </span>
                              )}
                            </div>
                          ) : (
                            <QuantidadeCalculada
                              item={item}
                              servico={servico}
                              resolvida={resolvida}
                              dispatch={dispatch}
                            />
                          )}
                        </TableCell>
                        {precos !== undefined && (
                          <PrecoDaLinha
                            item={item}
                            quantidade={resolvida.quantity}
                            precos={precos}
                            {...(custo === undefined ? {} : { custo })}
                          />
                        )}
                        <TableCell>
                          {!travado && (
                            <div className="flex items-center justify-end gap-1">
                              {editada &&
                                resolvida.fromSheet !== undefined &&
                                resolvida.calculated === undefined &&
                                resolvida.fromPieces === undefined && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Voltar ${item.description} à quantidade da planilha`}
                                    onClick={() =>
                                      dispatch({
                                        type: 'CLEAR_ITEM_QUANTITY',
                                        serviceKey: servico.key,
                                        itemKey: item.key,
                                      })
                                    }
                                  >
                                    <RotateCcw className="size-4" />
                                  </Button>
                                )}
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Remover ${item.description} de ${servico.name}`}
                                onClick={() =>
                                  dispatch({
                                    type: 'REMOVE_SERVICE_ITEM',
                                    serviceKey: servico.key,
                                    itemKey: item.key,
                                  })
                                }
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableWrapper>
          )}

          {!travado && <NovoItem serviceKey={servico.key} funcoes={funcoes} dispatch={dispatch} />}
        </div>
      )}
    </li>
  );
}

/**
 * O preço do material e o total da linha, só para ler (Fase 29, D15).
 *
 * O preço é o do material na obra, pela chave da consolidação (D7): as linhas do mesmo
 * material em serviços diferentes mostram o mesmo número, e decidi-lo é na consolidação, onde
 * o material é uma linha só. A mão de obra mostra o R$/h da função (etapa 3), pela mesma
 * chave — e sem o custo da obra, "—".
 */
function PrecoDaLinha({
  item,
  quantidade,
  precos,
  custo,
}: {
  item: CatalogService['items'][number];
  quantidade: number | undefined;
  precos: PrecosDaObraNaTela;
  custo?: CustoDaMaoDeObraNaTela;
}) {
  const celula = 'hidden text-right md:table-cell';
  if (item.kind === 'MAO_DE_OBRA') {
    return <CustoDaHoraDaLinha item={item} quantidade={quantidade} custo={custo} />;
  }
  if (precos.precos === undefined) {
    // Lendo, "…"; a leitura que falhou é dita na consolidação, e aqui fica "—".
    const espera = precos.erro === undefined ? '…' : '—';
    return (
      <>
        <TableCell className={cn(celula, 'text-(--color-content-muted)')}>{espera}</TableCell>
        <TableCell className={cn(celula, 'text-(--color-content-muted)')}>{espera}</TableCell>
      </>
    );
  }
  // O fator do produto vale aqui como na consolidação: a mesma linha, o mesmo preço (D7).
  const resolvido = resolveMaterialPrice(
    precos.precos.get(consolidationKey(item)),
    item,
    fatorDoProdutoDe(precos.fatoresDoProduto, item),
  );
  const unitario = resolvido.unitPrice;
  return (
    <>
      <TableCell className={celula}>
        <span className="flex flex-col items-end">
          <span className="tabular">{unitario === undefined ? '—' : precoUnitario(unitario)}</span>
          {resolvido.origin !== undefined && (
            <span
              className="text-[11px] text-(--color-content-muted)"
              title={ORIGIN_LABEL[resolvido.origin]}
            >
              {ROTULO_CURTO_DA_ORIGEM[resolvido.origin].toLocaleLowerCase('pt-BR')}
            </span>
          )}
          {resolvido.warning !== undefined && (
            <span className="text-[11px] text-(--color-state-warn)">
              {WARNING_LABEL[resolvido.warning]}
            </span>
          )}
        </span>
      </TableCell>
      <TableCell className={cn(celula, 'tabular')}>
        {unitario === undefined || quantidade === undefined
          ? '—'
          : reais(materialTotal(quantidade, unitario))}
      </TableCell>
    </>
  );
}

/**
 * O R$/h da função e o total da linha de mão de obra, só para ler (Fase 29, etapa 3).
 *
 * A chave é a da consolidação — a função; sem função, a descrição (RN-252) —, e a regra é a
 * mesma da consolidação (`resolveLaborItemRate`): as linhas da mesma função em serviços
 * diferentes mostram o mesmo R$/h. O provisório se decide lá, onde a função é uma linha só.
 */
function CustoDaHoraDaLinha({
  item,
  quantidade,
  custo,
}: {
  item: CatalogService['items'][number];
  quantidade: number | undefined;
  custo: CustoDaMaoDeObraNaTela | undefined;
}) {
  const celula = 'hidden text-right md:table-cell';
  const taxas = custo?.custo?.taxas;
  if (taxas === undefined) {
    // Sem o custo da obra, "—"; lendo, "…" — a leitura que falhou é dita na consolidação.
    const espera = custo !== undefined && custo.erro === undefined ? '…' : '—';
    return (
      <>
        <TableCell className={cn(celula, 'text-(--color-content-muted)')}>{espera}</TableCell>
        <TableCell className={cn(celula, 'text-(--color-content-muted)')}>{espera}</TableCell>
      </>
    );
  }
  const taxa = resolveLaborItemRate(
    { key: consolidationKey(item), ...(item.unit === undefined ? {} : { unit: item.unit }) },
    taxas,
  );
  const selo = seloDaFonte(taxa);
  return (
    <>
      <TableCell className={celula}>
        <span className="flex flex-col items-end">
          <span className="tabular">
            {taxa.perHour === undefined ? '—' : `${precoUnitario(taxa.perHour)}/h`}
          </span>
          {taxa.source !== undefined && selo !== undefined && (
            <span
              className="text-[11px] text-(--color-content-muted)"
              title={LABOR_SOURCE_LABEL[taxa.source]}
            >
              {selo.toLocaleLowerCase('pt-BR')}
            </span>
          )}
          {taxa.warning !== undefined && (
            <span className="text-[11px] text-(--color-state-warn)">
              {LABOR_WARNING_LABEL[taxa.warning]}
            </span>
          )}
        </span>
      </TableCell>
      <TableCell className={cn(celula, 'tabular')}>
        {taxa.perHour === undefined || quantidade === undefined
          ? '—'
          : reais(custoDasHoras(quantidade, taxa.perHour))}
      </TableCell>
    </>
  );
}

/**
 * A função do item de mão de obra, escolhida da lista — e não digitada (RN-252).
 *
 * A mão de obra da planilha chega como texto ("PEDREIRO", "M.O. ALVENARIA") e fica "sem
 * função" até alguém escolher. A lista é a das funções que executam a disciplina do
 * serviço; a que já estava escolhida e saiu da disciplina depois continua aparecendo, com
 * o aviso — trocar o cadastro de funções não reescreve o item calado.
 */
function FuncaoDoItem({
  item,
  funcoes,
  nomeDaFuncao,
  onEscolher,
}: {
  item: CatalogService['items'][number];
  funcoes: readonly FuncaoDoServico[];
  nomeDaFuncao: (jobRoleId: string) => string | undefined;
  onEscolher: (jobRoleId: JobRoleId | undefined) => void;
}) {
  const atual = item.jobRoleId;
  const foraDaLista = atual !== undefined && !funcoes.some((funcao) => funcao.id === atual);

  return (
    <select
      className={`${SELECT_CLASS} mt-1 w-full max-w-72`}
      aria-label={`Função de ${item.description}`}
      value={atual ?? ''}
      onChange={(event) =>
        onEscolher(event.target.value === '' ? undefined : asId<'JobRole'>(event.target.value))
      }
    >
      <option value="">sem função</option>
      {foraDaLista && (
        <option value={atual}>
          {`${nomeDaFuncao(atual) ?? 'Função'} (não executa esta disciplina)`}
        </option>
      )}
      {funcoes.map((funcao) => (
        <option key={funcao.id} value={funcao.id}>
          {funcao.name}
        </option>
      ))}
    </select>
  );
}

// ── A conversão (Fase 24, fatia 6) ──────────────────────────────────────────────

/**
 * O índice, a perda e o que a conta dá — três células da linha do item.
 *
 * O índice diz a unidade junto ("SC/m²", "HH/m³"): 0,4 sem unidade seria 0,4 de quê por quê,
 * e a pergunta que o índice responde é justamente essa. Ele é do cadastro do item e vale para
 * todas as tipologias (D7); a perda fica ao lado, e a mão de obra não tem (D8).
 *
 * Índice vazio é "sem índice", e nasce assim na mão de obra também: a sugestão de
 * produtividade (fatia 7) aparece ao lado da função e só vira índice no "Usar", e zero
 * calculado apagaria a hora da planilha.
 */
function ConversaoDoItem({
  item,
  servico,
  unidadeDoMedido,
  calculado,
  participacao,
  travado = false,
  porTipo = [],
  mediaHoje,
  dispatch,
}: {
  item: CatalogService['items'][number];
  servico: CatalogService;
  unidadeDoMedido: string;
  calculado: number | undefined;
  /** A fração do aço que é desta bitola — só no item de aço (Fase 27, RN-378). */
  participacao?: number | undefined;
  /** O serviço foi aprovado no orçamento: o índice e a perda só se leem (Fase 33, RN-438). */
  travado?: boolean;
  /**
   * Na estaca, o fator de cada tipo desenhado (Fase 39, RN-508). Vazio: o índice é o da obra,
   * como antes.
   */
  porTipo?: readonly FatorDoTipo[];
  /** A média do apartamento de hoje para o item (Fase 40, RN-520), como a foto que ele guardaria. */
  mediaHoje?: IndexAverage | undefined;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const maoDeObra = item.kind === 'MAO_DE_OBRA';
  const unidadeDoItem = maoDeObra ? 'HH' : (item.unit ?? 'un');
  return (
    <>
      <TableCell>
        {item.steelGauge !== undefined ? (
          // O item de aço não tem índice (RN-378): ele é o medido do CORTE E DOBRA vezes a
          // participação da bitola na taxa da disciplina. Campo nenhum aqui — a taxa se edita
          // no topo da disciplina, e vale para os três serviços de ferragem.
          <span className="flex flex-col text-xs">
            <span>pela taxa</span>
            <span className="text-(--color-content-muted)">
              {participacao === undefined
                ? 'sem taxa desta bitola'
                : `${metros(participacao * 100, 1)}% do aço`}
            </span>
          </span>
        ) : porTipo.length > 0 ? (
          <IndicePorTipo
            item={item}
            servico={servico}
            porTipo={porTipo}
            unidade={`${unidadeDoItem}/${unidadeDoMedido}`}
            travado={travado}
            dispatch={dispatch}
          />
        ) : travado ? (
          <span className="flex flex-col text-xs">
            <span className="tabular">
              {item.consumptionIndex === undefined ? '—' : metros(item.consumptionIndex, 4)}
            </span>
            <span className="text-(--color-content-muted)">
              {unidadeDoItem}/{unidadeDoMedido}
            </span>
          </span>
        ) : (
          <>
            <NumeroOpcionalInput
              aria-label={`Índice de ${item.description} em ${servico.name}`}
              placeholder="—"
              className="h-9 w-20 text-right"
              value={item.consumptionIndex}
              onChange={(consumptionIndex) =>
                dispatch({
                  type: 'SET_ITEM_INDEX',
                  serviceKey: servico.key,
                  itemKey: item.key,
                  ...(consumptionIndex === undefined ? {} : { consumptionIndex }),
                })
              }
            />
            <span className="mt-0.5 block text-xs text-(--color-content-muted)">
              {unidadeDoItem}/{unidadeDoMedido}
            </span>
            <OrigemDoIndice
              item={item}
              mediaHoje={mediaHoje}
              onUsar={(average) =>
                dispatch({
                  type: 'APPLY_INDEX_AVERAGE',
                  serviceKey: servico.key,
                  itemKey: item.key,
                  average,
                  follow: true,
                })
              }
            />
          </>
        )}
      </TableCell>
      <TableCell>
        {maoDeObra ? (
          <span className="text-xs text-(--color-content-muted)">—</span>
        ) : travado ? (
          <span className="tabular text-xs">
            {item.lossPercent === undefined ? '—' : metros(item.lossPercent, 3)}
          </span>
        ) : (
          <NumeroOpcionalInput
            aria-label={`Perda de ${item.description} em ${servico.name}, em por cento`}
            placeholder="0"
            className="h-9 w-16 text-right"
            value={item.lossPercent}
            onChange={(lossPercent) =>
              dispatch({
                type: 'SET_ITEM_LOSS',
                serviceKey: servico.key,
                itemKey: item.key,
                ...(lossPercent === undefined ? {} : { lossPercent }),
              })
            }
          />
        )}
      </TableCell>
      <TableCell className="tabular text-right text-(--color-content-muted)">
        {calculado === undefined ? '—' : metros(calculado, 3)}
      </TableCell>
    </>
  );
}

/**
 * De onde veio o índice (Fase 40, RN-520), embaixo do campo.
 *
 * O que segue a média diz de quantas obras ela veio. O digitado mostra a média de hoje ao lado,
 * com "Usar a média", quando ela é outra até a quarta casa — a que a tela mostra. Sem índice e
 * com média, a média aparece como sugestão. Sem média nenhuma, nada: a tela é a de antes.
 */
/**
 * De onde veio a taxa de uma bitola (Fase 40, etapa 3 — RN-533): "média · N obras" para a que
 * segue a média; a média de hoje ao lado da digitada, com o "Usar a média".
 */
function OrigemDaTaxa({
  rotulo,
  taxa,
  hoje,
  onUsar,
}: {
  rotulo: string;
  taxa: SteelRate | undefined;
  hoje: ApartmentSteelAverage | undefined;
  onUsar: (hoje: ApartmentSteelAverage) => void;
}) {
  if (taxa?.origin === 'MEDIA') {
    const foto = taxa.average;
    return (
      <span className="text-(--color-content-muted)">
        média
        {foto === undefined
          ? ''
          : ` · ${String(foto.projects)} ${foto.projects === 1 ? 'obra' : 'obras'}`}
      </span>
    );
  }
  if (hoje === undefined) return null;
  if (taxa !== undefined && Math.abs(hoje.rate - taxa.kgPerM3) < 0.0005) return null;
  return (
    <span className="flex flex-wrap items-center justify-end gap-x-1 text-(--color-content-muted)">
      {taxa === undefined ? '' : 'digitada · '}média{' '}
      <span className="tabular">{metros(hoje.rate, 3)}</span>
      <button
        type="button"
        className="font-medium text-(--color-brand) underline-offset-2 hover:underline"
        aria-label={`Usar a média em ${rotulo}`}
        onClick={() => onUsar(hoje)}
      >
        Usar
      </button>
    </span>
  );
}

function OrigemDoIndice({
  item,
  mediaHoje,
  onUsar,
}: {
  item: CatalogService['items'][number];
  mediaHoje: IndexAverage | undefined;
  onUsar: (average: IndexAverage) => void;
}) {
  if (item.indexOrigin === 'MEDIA') {
    const foto = item.indexAverage;
    return (
      <span className="mt-0.5 block text-xs text-(--color-content-muted)">
        média
        {foto === undefined
          ? ''
          : ` · ${String(foto.projects)} ${foto.projects === 1 ? 'obra' : 'obras'}`}
      </span>
    );
  }
  if (mediaHoje === undefined) return null;
  if (item.consumptionIndex !== undefined && mesmaMedia(mediaHoje.rate, item.consumptionIndex)) {
    return null;
  }
  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-(--color-content-muted)">
      {item.consumptionIndex === undefined ? '' : 'digitado · '}média{' '}
      <span className="tabular">{metros(mediaHoje.rate, 4)}</span>
      <button
        type="button"
        className="font-medium text-(--color-brand) underline-offset-2 hover:underline"
        aria-label={`Usar a média em ${item.description}`}
        onClick={() => onUsar(mediaHoje)}
      >
        Usar a média
      </button>
    </span>
  );
}

/**
 * O índice do item da estaca, tipo a tipo (Fase 39, RN-508): um índice só não comporta dois
 * diâmetros. Cada tipo desenhado mostra o fator dele, que muda nos Tipos de estaca. O campo do
 * índice da obra só aparece quando algum tipo não tem fator para o item — é ele que vale ali.
 */
function IndicePorTipo({
  item,
  servico,
  porTipo,
  unidade,
  travado,
  dispatch,
}: {
  item: CatalogService['items'][number];
  servico: CatalogService;
  porTipo: readonly FatorDoTipo[];
  unidade: string;
  travado: boolean;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const reserva = algumSemFator(porTipo);
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span>por tipo</span>
      {porTipo.map((tipo) => (
        <span key={tipo.tipoId} className="tabular text-(--color-content-muted)">
          {tipo.nome} {tipo.fator === undefined ? 'índice da obra' : textoDoFator(tipo.fator)}
        </span>
      ))}
      {reserva &&
        (travado ? (
          <span className="tabular">
            obra {item.consumptionIndex === undefined ? '—' : metros(item.consumptionIndex, 4)}
          </span>
        ) : (
          <NumeroOpcionalInput
            aria-label={`Índice da obra de ${item.description} em ${servico.name}, nos tipos sem fator`}
            placeholder="—"
            className="h-9 w-20 text-right"
            value={item.consumptionIndex}
            onChange={(consumptionIndex) =>
              dispatch({
                type: 'SET_ITEM_INDEX',
                serviceKey: servico.key,
                itemKey: item.key,
                ...(consumptionIndex === undefined ? {} : { consumptionIndex }),
              })
            }
          />
        ))}
      <span className="text-(--color-content-muted)">{unidade}</span>
    </span>
  );
}

/**
 * A quantidade do item do serviço aprovado, só para ler (Fase 33, RN-438): o número e de onde
 * ele veio, sem campo, sem "Discordar" e sem a volta à planilha.
 */
function QuantidadeTravada({ resolvida }: { resolvida: ResolvedQuantity }) {
  if (resolvida.quantity === undefined) {
    return <span className="text-xs text-(--color-content-muted)">sem quantidade</span>;
  }
  return (
    <span className="tabular flex flex-col gap-0.5">
      {metros(resolvida.quantity, 3)}
      <span className="text-xs text-(--color-content-muted)">
        {ROTULO_DA_ORIGEM[resolvida.origin]}
        {resolvida.origin === 'MANUAL' && resolvida.reason !== undefined
          ? ` · “${resolvida.reason}”`
          : ''}
      </span>
    </span>
  );
}

/**
 * A quantidade do item que o índice calcula — e a discordância dela, com motivo (D9).
 *
 * O calculado não é campo solto: ele é o medido vezes o índice, e digitar por cima é
 * discordar da conta. Por isso "Discordar" abre a correção, que só aplica com o motivo, e o
 * digitado aparece depois com a marca, o motivo, o calculado ao lado e a volta à conta.
 *
 * A quantidade da planilha por peça (Fase 28, `PECAS`) entra pelo mesmo caminho: é o número
 * pronto das peças importadas, somado ao que o índice calcula das outras, e discordar dele
 * também pede o motivo. A base da discordância é a soma das duas partes — é dela que o
 * digitado discorda.
 */
function QuantidadeCalculada({
  item,
  servico,
  resolvida,
  dispatch,
}: {
  item: CatalogService['items'][number];
  servico: CatalogService;
  resolvida: ResolvedQuantity;
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [discordando, setDiscordando] = useState(false);
  const rotulo = `Quantidade de ${item.description} em ${servico.name}`;
  const dasPecas = resolvida.fromPieces;
  /** O número que o digitado contraria: o calculado, mais as peças quando há. */
  const base =
    dasPecas === undefined ? resolvida.calculated : dasPecas + (resolvida.calculated ?? 0);
  const deQue = dasPecas === undefined ? 'do calculado' : `da ${ROTULO_DAS_PECAS}`;

  if (discordando) {
    return (
      <CorrecaoComMotivo
        rotulo={rotulo}
        inicial={resolvida.quantity}
        {...(resolvida.reason === undefined ? {} : { motivoInicial: resolvida.reason })}
        onCancelar={() => setDiscordando(false)}
        onAplicar={(quantity, reason) => {
          dispatch({
            type: 'SET_ITEM_QUANTITY',
            serviceKey: servico.key,
            itemKey: item.key,
            quantity,
            reason,
            ...(base === undefined ? {} : { calculated: base }),
          });
          setDiscordando(false);
        }}
      />
    );
  }

  const digitado = resolvida.origin === 'MANUAL';
  // As duas partes, quando há as duas: "planilha por peça 33,180 + calculado 2,000".
  const partes = [
    dasPecas === undefined ? undefined : `${ROTULO_DAS_PECAS} ${metros(dasPecas, 3)}`,
    resolvida.calculated === undefined ? undefined : `calculado ${metros(resolvida.calculated, 3)}`,
  ].filter((parte) => parte !== undefined);
  return (
    <div className="flex flex-col gap-0.5">
      <span className="tabular flex items-center gap-1.5">
        {metros(resolvida.quantity ?? 0, 3)}
        <span
          className={cn(
            'text-xs',
            digitado ? 'text-(--color-state-info)' : 'text-(--color-content-muted)',
          )}
          {...(resolvida.origin === 'PECAS' ? { title: EXPLICACAO_DAS_PECAS } : {})}
        >
          {ROTULO_DA_ORIGEM[resolvida.origin]}
        </span>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Discordar ${deQue} de ${item.description}`}
          onClick={() => setDiscordando(true)}
        >
          <Pencil className="size-4" />
        </Button>
        {digitado && (
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Voltar ${item.description} ${dasPecas === undefined ? 'ao calculado' : `à ${ROTULO_DAS_PECAS}`}`}
            onClick={() =>
              dispatch({ type: 'CLEAR_ITEM_QUANTITY', serviceKey: servico.key, itemKey: item.key })
            }
          >
            <RotateCcw className="size-4" />
          </Button>
        )}
      </span>
      {(digitado || partes.length > 1) && (
        <span
          className="text-xs text-(--color-content-muted)"
          {...(dasPecas === undefined ? {} : { title: EXPLICACAO_DAS_PECAS })}
        >
          {partes.join(' + ')}
          {digitado && resolvida.reason !== undefined ? ` · “${resolvida.reason}”` : ''}
        </span>
      )}
    </div>
  );
}

// ── Acrescentar material ────────────────────────────────────────────────────────

/**
 * O material entra pelo catálogo de compras, e só à mão quando ele não tem.
 *
 * A ordem importa e é o ponto desta tela: escolhendo da lista (`BuscaNoCompras`), o item
 * nasce com o nome e o SKU que o almoxarifado usa, e a consolidação, que une material pelo
 * **código de compras** (RN-011), soma uma linha onde há um material.
 *
 * ## A mão continua aberta
 *
 * Porque tem de continuar: nem toda obra tem a integração ligada, e material que ainda
 * não está no catálogo de lá é caso comum, não exceção. O catálogo é atalho, nunca
 * cancela.
 */
function NovoItem({
  serviceKey,
  funcoes,
  dispatch,
}: {
  serviceKey: string;
  /** As funções que executam a disciplina deste serviço — a lista da mão de obra. */
  funcoes: readonly FuncaoDoServico[];
  dispatch: React.Dispatch<WorkspaceAction>;
}) {
  const [description, setDescription] = useState('');
  const [code, setCode] = useState('');
  const [unit, setUnit] = useState('');
  const [kind, setKind] = useState<ItemKind>('MATERIAL');
  const [jobRoleId, setJobRoleId] = useState<JobRoleId | undefined>(undefined);
  // `undefined`, e não zero: vazio quer dizer "segue a planilha", e zero quer dizer que
  // alguém zerou. As duas se resolvem de formas diferentes ao reimportar o arquivo.
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  // Cada item acrescentado começa uma busca nova: a chave remonta o seletor, e o termo e a
  // lista de quem acabou de acrescentar não sobram para o próximo.
  const [rodada, setRodada] = useState(0);

  const ehMaoDeObra = kind === 'MAO_DE_OBRA';

  const escolher = (produto: { description: string; code: string; unit?: string }): void => {
    setDescription(produto.description);
    setCode(produto.code);
    setUnit(produto.unit ?? '');
  };

  /** A mão de obra só entra com a função escolhida — o texto livre saiu (RN-252). */
  const podeAcrescentar = ehMaoDeObra ? jobRoleId !== undefined : description.trim() !== '';

  const acrescentar = (): void => {
    if (!podeAcrescentar) return;
    dispatch({
      type: 'ADD_SERVICE_ITEM',
      serviceKey,
      description,
      kind,
      // Mão de obra mede em homem-hora, e não no que estiver digitado: é a unidade da
      // regra (RN-166), e deixá-la livre abriria a porta para lançar pedreiro em m².
      unit: ehMaoDeObra ? 'HH' : unit,
      // E não tem código de compras: não se compra hora de pedreiro do fornecedor.
      code: ehMaoDeObra ? '' : code,
      ...(ehMaoDeObra && jobRoleId !== undefined ? { jobRoleId } : {}),
      // Ausente segue a planilha; presente é o número que esta tipologia lança. Quem sabe
      // o material sabe quanto dele vai ali, e separar as duas coisas obrigava a procurar
      // a linha recém-criada na tabela para dizer o resto.
      ...(quantity === undefined ? {} : { quantity }),
    });
    setDescription('');
    setCode('');
    setUnit('');
    setJobRoleId(undefined);
    setQuantity(undefined);
    setRodada((atual) => atual + 1);
  };

  /**
   * A função escolhida dá nome ao item: é assim que ele aparece na lista e na soma.
   * O nome é da função, e não digitado — dois "pedreiro" escritos de dois jeitos seriam
   * duas linhas de hora para a mesma gente.
   */
  const escolherFuncao = (id: string): void => {
    const escolhida = funcoes.find((funcao) => funcao.id === id);
    setJobRoleId(escolhida?.id);
    setDescription(escolhida?.name ?? '');
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-(--color-border-subtle) p-3">
      <p className="text-xs font-medium text-(--color-content-muted)">Acrescentar material</p>

      {/*
        Mão de obra não procura no catálogo de compras: não se compra hora de pedreiro do
        fornecedor (RN-166), e oferecer a busca ali seria prometer resposta a uma pergunta
        que aquela base não responde. Desmontado, o seletor não consulta nada.
      */}
      {!ehMaoDeObra && <BuscaNoCompras key={rodada} onEscolher={escolher} />}

      {ehMaoDeObra && funcoes.length === 0 && (
        <p className="text-xs text-(--color-state-warn)">
          Nenhuma função executa a disciplina deste serviço. Ligue a função à disciplina em Mão de
          obra › Funções, e ela aparece aqui.
        </p>
      )}

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          acrescentar();
        }}
      >
        {ehMaoDeObra ? (
          <select
            aria-label="Função a acrescentar"
            className={`${SELECT_CLASS} h-9 min-w-56 flex-1`}
            value={jobRoleId ?? ''}
            disabled={funcoes.length === 0}
            onChange={(event) => escolherFuncao(event.target.value)}
          >
            <option value="">Escolha a função</option>
            {funcoes.map((funcao) => (
              <option key={funcao.id} value={funcao.id}>
                {funcao.name}
              </option>
            ))}
          </select>
        ) : (
          <Input
            aria-label="Material a acrescentar"
            placeholder="Nome do material"
            className="h-9 min-w-56 flex-1"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        )}
        <select
          aria-label="Classe do item a acrescentar"
          className={`${SELECT_CLASS} w-36`}
          value={kind}
          onChange={(event) => {
            const nova = event.target.value as ItemKind;
            // Entrar ou sair da mão de obra troca o campo do nome: o que foi digitado para
            // material não é nome de função, e a função escolhida não é nome de material.
            // Entre as classes de compra, o nome digitado fica.
            if ((nova === 'MAO_DE_OBRA') !== ehMaoDeObra) {
              setDescription('');
              setJobRoleId(undefined);
            }
            setKind(nova);
          }}
        >
          {ITEM_KINDS.map((item) => (
            <option key={item} value={item}>
              {ITEM_KIND_LABEL[item]}
            </option>
          ))}
        </select>
        <Input
          aria-label="Código de compras do material"
          placeholder={ehMaoDeObra ? '—' : 'Código'}
          className="h-9 w-32"
          value={ehMaoDeObra ? '' : code}
          disabled={ehMaoDeObra}
          onChange={(event) => setCode(event.target.value)}
        />
        <Input
          aria-label="Unidade do material"
          placeholder="Un."
          className="h-9 w-20"
          value={ehMaoDeObra ? 'HH' : unit}
          disabled={ehMaoDeObra}
          onChange={(event) => setUnit(event.target.value)}
        />
        <NumeroOpcionalInput
          aria-label="Quantidade do item a acrescentar"
          placeholder="Qtd."
          className="h-9 w-24 text-right"
          value={quantity}
          onChange={setQuantity}
        />
        <Button type="submit" variant="secondary" size="sm" disabled={!podeAcrescentar}>
          <Plus className="size-4" /> Acrescentar
        </Button>
      </form>
    </div>
  );
}
